/**
 * SITE DOMAIN (docs/bim-plan/14 slice C): property lines, setbacks, easements —
 * commands + derivations for the `site` top map (see types.ts#SiteRecord for the
 * record model and why these are model-space records, not elements).
 *
 * Everything visual is DERIVED at read time:
 * - the buildable ENVELOPE from the property line + per-role setbacks
 *   (`siteEnvelope` — mitered per-edge offset, arc-true, with the always-valid
 *   half-plane-clip fallback: the exact two-tier strategy the Site Analysis tool
 *   proved, re-implemented here on the kernel's own arc-aware pathOps, never a
 *   port of that tool's screen-space canvas code);
 * - METES AND BOUNDS labels per edge (`siteEdgeInfos` — survey decimal feet +
 *   quadrant DMS bearings against TRUE north, `siteSettings.northRotationDeg`);
 * - the VERTICAL CROSSING (`siteCrossingsForVertical` — where a section/elevation
 *   line crosses the property line / envelope / easements, the grids-in-verticals
 *   model), which is what makes "are we inside our envelope" checkable in the
 *   views where it matters.
 */
import type { ModelDoc } from './doc'
import { getSiteRecord, listSiteRecords, maps, recordToYMap, transact } from './doc'
import type { NodeId, Path2D, Pt2, SiteEdgeRole, SiteId, SiteRecord } from './types'
import { newSiteId, newNodeId } from './ids'
import { DEFAULT_DESIGN_RAINFALL_IN_HR } from './drainageSizing'
import { edgeCount, offsetEdgesVar, pathArea, insertNode, type EdgeRef } from './pathOps'
import { bulgeToArc, arcMidpoint, arcLengthOf, flattenPathPts, bulgeFromSagPoint } from './arc'

// ---------- site settings (the settings-map singleton, beside siteDatum) ----------

const SITE_SETTINGS_KEY = 'siteSettings'

export interface SiteSettings {
  /**
   * Degrees CCW from project +Y to TRUE north. Bearings are meaningless without
   * this — `compassName` and every plan convention assume project +Y ≈ north, and
   * this is the one place that assumption becomes explicit and adjustable (the
   * survey's basis of bearings rarely matches project north exactly).
   */
  northRotationDeg: number
  /**
   * Slice E (resolved Q9): the project's design rainfall (in/hr) for gutter/
   * downspout sizing — the jurisdiction's 100-yr 1-hr value. Default is the
   * PROVISIONAL Laguna Beach number (drainageSizing.ts).
   */
  designRainfallInHr: number
  /**
   * TRUE NORTH round: the site's street address + geocoded WGS84 coordinates
   * (Mapbox, entered/located in SiteSettingsModal). Coordinates feed the planned
   * solar-orientation/shade-study and energy-calculation features — stored on the
   * MODEL (not re-geocoded per feature) so they're settable once, correctable by
   * hand, and available offline from the doc alone.
   */
  address: string
  latitude: number | null
  longitude: number | null
  /**
   * SOLAR STUDY round (2026-08-22, Matt: "a modal dialogue to set the date and time
   * to run solar studies"): the model-wide sun. 'default' = the fixed, view-friendly
   * studio sun the 3D viewport always had; 'solarStudy' = the REAL sun computed from
   * latitude/longitude/northRotationDeg + sunDate/sunTime (bim-geometry sunMath.ts).
   * Elevations are deliberately OUTSIDE this switch by default — each uses its own
   * artistic 45°-from-lookDir sun (the drafting convention) unless that view's
   * ViewRecord.sun.solarStudy opts it into this model-wide sun.
   */
  sunMode: 'default' | 'solarStudy'
  /** ISO 'YYYY-MM-DD' the Solar Study evaluates at. */
  sunDate: string
  /** Clock hour 0–24 (fractional = minutes) the Solar Study evaluates at. */
  sunTime: number
  /** Real-time shadows in the 3D viewport + elevation backdrops (per-renderer cost —
   *  off by default; sceneStore.js only configures shadow maps once first enabled). */
  shadowsEnabled: boolean
  /** How dark a cast shadow reads, 0 (invisible) – 1 (full occlusion) — maps straight
   *  onto THREE.LightShadow.intensity, purpose-built for exactly this. */
  shadowDarkness: number
}

/** Neutral default study moment: summer solstice, 10 AM — a bright, high-sun,
 *  long-enough-shadow moment that reads well the first time the modal opens. */
export const SUN_DATE_DEFAULT = '2026-06-21'
export const SUN_TIME_DEFAULT = 10
export const SHADOW_DARKNESS_DEFAULT = 0.75

export function getSiteSettings(model: ModelDoc): SiteSettings {
  const v = maps(model).settings.get(SITE_SETTINGS_KEY) as Partial<SiteSettings> | undefined
  return {
    northRotationDeg: typeof v?.northRotationDeg === 'number' ? v.northRotationDeg : 0,
    designRainfallInHr: typeof v?.designRainfallInHr === 'number' && v.designRainfallInHr > 0
      ? v.designRainfallInHr
      : DEFAULT_DESIGN_RAINFALL_IN_HR,
    address: typeof v?.address === 'string' ? v.address : '',
    latitude: typeof v?.latitude === 'number' && isFinite(v.latitude) ? v.latitude : null,
    longitude: typeof v?.longitude === 'number' && isFinite(v.longitude) ? v.longitude : null,
    sunMode: v?.sunMode === 'solarStudy' ? 'solarStudy' : 'default',
    sunDate: typeof v?.sunDate === 'string' && v.sunDate ? v.sunDate : SUN_DATE_DEFAULT,
    sunTime: typeof v?.sunTime === 'number' && isFinite(v.sunTime) ? v.sunTime : SUN_TIME_DEFAULT,
    shadowsEnabled: v?.shadowsEnabled === true,
    shadowDarkness: typeof v?.shadowDarkness === 'number' && isFinite(v.shadowDarkness)
      ? Math.max(0, Math.min(1, v.shadowDarkness))
      : SHADOW_DARKNESS_DEFAULT,
  }
}

export function setSiteSettings(model: ModelDoc, patch: Partial<SiteSettings>): void {
  transact(model, () => {
    maps(model).settings.set(SITE_SETTINGS_KEY, { ...getSiteSettings(model), ...patch })
  })
}

// ---------- commands ----------

export function createSiteRecord(
  model: ModelDoc,
  input: { kind: SiteRecord['kind']; path: Path2D; name?: string },
): SiteId {
  const id = newSiteId()
  transact(model, () => {
    maps(model).site.set(id, recordToYMap({
      id, kind: input.kind, path: input.path,
      ...(input.name ? { name: input.name } : {}),
    }))
  })
  return id
}

/**
 * Patch-merge; the default-never-stored discipline (the grids convention): an empty
 * object / empty string clears its key rather than storing a hollow value.
 */
export function updateSiteRecord(
  model: ModelDoc,
  id: SiteId,
  patch: Partial<Pick<SiteRecord, 'name' | 'path' | 'edgeRoles' | 'roleSetbacksFt' | 'edgeSetbacksFt'>>,
): boolean {
  const m = maps(model).site.get(id)
  if (!m) return false
  transact(model, () => {
    for (const [k, v] of Object.entries(patch)) {
      if (v === undefined) continue
      const empty = (typeof v === 'string' && !v.trim()) ||
        (typeof v === 'object' && v !== null && !Array.isArray(v) && k !== 'path' && Object.keys(v).length === 0)
      if (empty) m.delete(k)
      else m.set(k, v)
    }
  })
  return true
}

export function deleteSiteRecord(model: ModelDoc, id: SiteId): boolean {
  const m = maps(model).site
  if (!m.has(id)) return false
  transact(model, () => { m.delete(id) })
  return true
}

/** rigid translate (the pet-palette Move) — every node shifts, keys untouched */
export function moveSiteRecord(model: ModelDoc, id: SiteId, d: Pt2): boolean {
  const rec = getSiteRecord(model, id)
  if (!rec) return false
  const path: Path2D = {
    closed: rec.path.closed,
    nodes: rec.path.nodes.map((n) => ({ ...n, pt: [n.pt[0] + d[0], n.pt[1] + d[1]] as Pt2 })),
  }
  return updateSiteRecord(model, id, { path })
}

/**
 * TRUE NORTH round (Matt: "move the property line bounds as a whole to get it
 * aligned with a background survey, because that's much easier than moving the
 * whole model"): rigid translate of EVERY site record — the parcel and its
 * easements are one survey dataset and move as a set. One transaction = one
 * undo step. Returns how many records moved.
 */
export function moveSiteRecords(model: ModelDoc, d: Pt2): number {
  const recs = listSiteRecords(model)
  if (!recs.length) return 0
  transact(model, () => {
    for (const rec of recs) moveSiteRecord(model, rec.id, d)
  })
  return recs.length
}

/**
 * TRUE NORTH round — "rotate the world around the model" (Matt: existing
 * projects must never have their geometry rotated to fix north). Rotates EVERY
 * site record about `center` by `deltaDeg` CCW AND advances `northRotationDeg`
 * by the same delta, in ONE transaction. Because a bearing derives from
 * (edge direction, northRotationDeg) and both rotate together, every
 * metes-and-bounds label is INVARIANT under this op — the parcel swings around
 * the fixed model until it aligns, and the survey stays true throughout.
 * Bulges are rotation-invariant (they encode sweep relative to the chord), so
 * arc frontages ride along untouched. North is normalized to (-180, 180].
 * Returns how many records rotated.
 */
export function rotateSiteWorld(model: ModelDoc, center: Pt2, deltaDeg: number): number {
  const recs = listSiteRecords(model)
  const rad = (deltaDeg * Math.PI) / 180
  const c = Math.cos(rad), s = Math.sin(rad)
  const rot = (p: Pt2): Pt2 => [
    center[0] + (p[0] - center[0]) * c - (p[1] - center[1]) * s,
    center[1] + (p[0] - center[0]) * s + (p[1] - center[1]) * c,
  ]
  transact(model, () => {
    for (const rec of recs) {
      updateSiteRecord(model, rec.id, {
        path: { closed: rec.path.closed, nodes: rec.path.nodes.map((n) => ({ ...n, pt: rot(n.pt) })) },
      })
    }
    const north = getSiteSettings(model).northRotationDeg + deltaDeg
    const norm = ((north % 360) + 540) % 360 - 180   // (-180, 180]
    setSiteSettings(model, { northRotationDeg: norm === -180 ? 180 : norm })
  })
  return recs.length
}

/**
 * Split an edge at a point, INHERITING the split edge's role and per-edge setback
 * onto the new second half (roles key by from-node — without this copy, splitting
 * the front lot line would silently strip the new half's setback).
 *
 * `curved` (VERTEX EDITING round — the mid-grip "New node (curved)" option): when
 * the split edge was straight, gives the FIRST new sub-edge (the original
 * from-node's leaving edge, now shorter) a modest default sag — the same
 * insertMidNode math the drafting-path mid-grip already uses. Deliberately does
 * NOT recurve an edge that's already an arc (an arc edge's split halves already
 * re-derive about the original circle inside `insertNode` — this flag is only
 * for "make a straight edge curved," never a re-bow).
 */
export function insertSiteNode(
  model: ModelDoc, id: SiteId, edge: EdgeRef, pt: Pt2, curved = false,
): NodeId | null {
  const rec = getSiteRecord(model, id)
  if (!rec) return null
  const res = insertNode(rec.path, edge, pt)
  if (!res) return null
  const { path, nodeId } = res
  let nodes = path.nodes
  if (curved) {
    const fromIdx = nodes.findIndex((nd) => nd.id === edge.fromNodeId)
    const from = fromIdx >= 0 ? nodes[fromIdx] : null
    if (from && !from.bulge) {
      const dx = pt[0] - from.pt[0], dy = pt[1] - from.pt[1]
      const len = Math.hypot(dx, dy) || 1
      const sagPt: Pt2 = [pt[0] - (dy / len) * (len * 0.12), pt[1] + (dx / len) * (len * 0.12)]
      const bulge = bulgeFromSagPoint(from.pt, pt, sagPt)
      if (Math.abs(bulge) > 1e-4) nodes = nodes.map((nd, i) => (i === fromIdx ? { ...nd, bulge } : nd))
    }
  }
  const patch: Parameters<typeof updateSiteRecord>[2] = { path: { ...path, nodes } }
  const role = rec.edgeRoles?.[edge.fromNodeId]
  if (role) patch.edgeRoles = { ...rec.edgeRoles, [nodeId]: role }
  const sb = rec.edgeSetbacksFt?.[edge.fromNodeId]
  if (sb != null) patch.edgeSetbacksFt = { ...rec.edgeSetbacksFt, [nodeId]: sb }
  updateSiteRecord(model, id, patch)
  return nodeId
}

/**
 * Delete a node (VERTEX EDITING round — a corner grip's Alt+click, the pathNode
 * convention). A closed ring stays a polygon — refuses below 3 nodes, the same
 * floor the drafting-path node grip enforces for a closed loop/region. Prunes
 * the deleted node's role/setback-override entries so they never orphan silently
 * (the insertSiteNode inheritance above is the mirror-image concern).
 */
export function deleteSiteNode(model: ModelDoc, id: SiteId, nodeId: NodeId): boolean {
  const rec = getSiteRecord(model, id)
  if (!rec || rec.path.nodes.length <= 3) return false
  const nodes = rec.path.nodes.filter((nd) => nd.id !== nodeId)
  if (nodes.length === rec.path.nodes.length) return false
  const patch: Parameters<typeof updateSiteRecord>[2] = { path: { ...rec.path, nodes } }
  if (rec.edgeRoles && nodeId in rec.edgeRoles) {
    const next = { ...rec.edgeRoles }
    delete next[nodeId]
    patch.edgeRoles = next
  }
  if (rec.edgeSetbacksFt && nodeId in rec.edgeSetbacksFt) {
    const next = { ...rec.edgeSetbacksFt }
    delete next[nodeId]
    patch.edgeSetbacksFt = next
  }
  return updateSiteRecord(model, id, patch)
}

// ---------- setback resolution + the buildable envelope ----------

/** one setback distance per edge index: per-edge override → role's distance → 0 */
export function resolveEdgeSetbacksFt(rec: SiteRecord): number[] {
  const n = edgeCount(rec.path)
  const out: number[] = []
  for (let e = 0; e < n; e++) {
    const from = rec.path.nodes[e].id
    const override = rec.edgeSetbacksFt?.[from]
    if (override != null) { out.push(Math.max(0, override)); continue }
    const role = rec.edgeRoles?.[from]
    out.push(Math.max(0, (role && rec.roleSetbacksFt?.[role]) || 0))
  }
  return out
}

/** O(n²) segment self-intersection over a closed flattened ring (adjacent edges skip) */
function ringSelfIntersects(pts: Pt2[]): boolean {
  const n = pts.length
  if (n < 4) return false
  const seg = (i: number): [Pt2, Pt2] => [pts[i], pts[(i + 1) % n]]
  for (let i = 0; i < n; i++) {
    for (let j = i + 2; j < n; j++) {
      if (i === 0 && j === n - 1) continue
      const [a, b] = seg(i), [c, d] = seg(j)
      const d1x = b[0] - a[0], d1y = b[1] - a[1]
      const d2x = d[0] - c[0], d2y = d[1] - c[1]
      const den = d1x * d2y - d1y * d2x
      if (Math.abs(den) < 1e-12) continue
      const t = ((c[0] - a[0]) * d2y - (c[1] - a[1]) * d2x) / den
      const u = ((c[0] - a[0]) * d1y - (c[1] - a[1]) * d1x) / den
      if (t > 1e-9 && t < 1 - 1e-9 && u > 1e-9 && u < 1 - 1e-9) return true
    }
  }
  return false
}

const ringArea = (pts: Pt2[]): number => {
  let a = 0
  for (let i = 0, j = pts.length - 1; i < pts.length; j = i++) {
    a += (pts[j][0] * pts[i][1] - pts[i][0] * pts[j][1]) / 2
  }
  return a
}

/** distance from p to the finite segment a→b */
function distToSegment(p: Pt2, a: Pt2, b: Pt2): number {
  const dx = b[0] - a[0], dy = b[1] - a[1]
  const L2 = dx * dx + dy * dy
  const t = L2 < 1e-18 ? 0 : Math.max(0, Math.min(1, ((p[0] - a[0]) * dx + (p[1] - a[1]) * dy) / L2))
  return Math.hypot(p[0] - (a[0] + dx * t), p[1] - (a[1] + dy * t))
}

/** even-odd point-in-ring */
function pointInRing(p: Pt2, ring: Pt2[]): boolean {
  let inside = false
  for (let i = 0, j = ring.length - 1; i < ring.length; j = i++) {
    const a = ring[i], b = ring[j]
    if ((a[1] > p[1]) !== (b[1] > p[1]) && p[0] < ((b[0] - a[0]) * (p[1] - a[1])) / (b[1] - a[1]) + a[0]) inside = !inside
  }
  return inside
}

/** Sutherland–Hodgman clip of a ring by one inward half-plane (n·p ≥ d keeps) */
function clipHalfPlane(poly: Pt2[], nx: number, ny: number, d: number): Pt2[] {
  const out: Pt2[] = []
  for (let i = 0, j = poly.length - 1; i < poly.length; j = i++) {
    const A = poly[j], B = poly[i]
    const fa = nx * A[0] + ny * A[1] - d
    const fb = nx * B[0] + ny * B[1] - d
    if (fa >= 0) {
      out.push(A)
      if (fb < 0) {
        const t = fa / (fa - fb)
        out.push([A[0] + (B[0] - A[0]) * t, A[1] + (B[1] - A[1]) * t])
      }
    } else if (fb >= 0) {
      const t = fa / (fa - fb)
      out.push([A[0] + (B[0] - A[0]) * t, A[1] + (B[1] - A[1]) * t])
    }
  }
  return out
}

/** flatten one edge (arc-aware) into its polyline, INCLUDING both endpoints */
function flatEdge(a: Pt2, b: Pt2, bulge: number | undefined): Pt2[] {
  if (!bulge) return [a, b]
  const pts = flattenPathPts({ closed: false, nodes: [{ id: 'a' as NodeId, pt: a, bulge }, { id: 'b' as NodeId, pt: b }] })
  return pts.length >= 2 ? pts : [a, b]
}

/** the record's flattened closed ring, each vertex tagged with its SOURCE edge index */
export function flattenSiteRing(rec: SiteRecord): { pts: Pt2[]; edgeOf: number[] } {
  const n = rec.path.nodes.length
  const nEdges = edgeCount(rec.path)
  const pts: Pt2[] = []
  const edgeOf: number[] = []
  for (let e = 0; e < nEdges; e++) {
    const a = rec.path.nodes[e].pt, b = rec.path.nodes[(e + 1) % n].pt
    const run = flatEdge(a, b, rec.path.nodes[e].bulge)
    for (let k = 0; k < run.length - 1; k++) { pts.push(run[k]); edgeOf.push(e) }
  }
  return { pts, edgeOf }
}

/**
 * The buildable envelope: the property line offset INWARD by each edge's resolved
 * setback. Tier 1 is the mitered per-edge offset (`pathOps.offsetEdgesVar` — arc
 * frontages stay concentric arcs); if its result self-intersects (spike miters on
 * extreme setback ratios), tier 2 falls back to the always-valid sequential
 * half-plane clip over the flattened ring — the legally-correct definition of a
 * setback envelope, structurally unable to produce an invalid polygon (the Site
 * Analysis lesson, both tiers). Null when the record has no setbacks at all, isn't
 * a closed property line, or the setbacks consume the whole parcel.
 */
export function siteEnvelope(rec: SiteRecord): Path2D | null {
  if (rec.kind !== 'propertyLine' || !rec.path.closed || rec.path.nodes.length < 3) return null
  const setbacks = resolveEdgeSetbacksFt(rec)
  if (!setbacks.some((d) => d > 0)) return null
  // THE legal definition: one inward half-plane per (flattened) boundary segment,
  // pushed in by its edge's setback — shared by tier 2 (which clips by them) and
  // tier 1's validity check (which verifies against them)
  const { pts, edgeOf } = flattenSiteRing(rec)
  if (pts.length < 3) return null
  const ccw = ringArea(pts) >= 0
  const planes: { nx: number; ny: number; d: number }[] = []
  for (let i = 0; i < pts.length; i++) {
    const dist = setbacks[edgeOf[i]]
    if (!(dist > 0)) continue
    const a = pts[i], b = pts[(i + 1) % pts.length]
    const dx = b[0] - a[0], dy = b[1] - a[1]
    const len = Math.hypot(dx, dy)
    if (len < 1e-9) continue
    // inward normal: LEFT of travel for CCW, right for CW
    const nx = (ccw ? -dy : dy) / len
    const ny = (ccw ? dx : -dx) / len
    planes.push({ nx, ny, d: nx * a[0] + ny * a[1] + dist })
  }
  // pathOps offsets move along the LEFT of travel — the INTERIOR side of a CCW
  // boundary (verified against leftNormal's actual math; the old offsetAllEdges
  // doc line claiming outward was wrong and is corrected there)
  const inwardSign = pathArea(rec.path) >= 0 ? 1 : -1
  const mitered = offsetEdgesVar(rec.path, setbacks.map((d) => d * inwardSign))
  if (mitered) {
    const flat = flattenPathPts(mitered)
    // validity: non-degenerate, no self-crossing, and every vertex INSIDE every
    // setback half-plane. The last check is the load-bearing one: setbacks that
    // consume the parcel can produce a cleanly-wound, non-self-intersecting
    // reflection rectangle that sits entirely OUTSIDE the legal envelope —
    // winding/self-intersection alone can't catch it (found by the consuming-
    // setbacks test). Tolerance covers arc flattening chords.
    // 0.05' tolerance: covers arc-flattening chord sag on both sides (the legal
    // planes are chordal approximations of curved frontages themselves)
    // CONCAVE-FRONTAGE FIX (2026-09-29, Matt — Santiago's front curves INTO the lot):
    // "legal" was tested against the infinite half-planes above, but a flattened arc
    // that bends inward extends its chord lines ACROSS the lot, so the correct
    // concentric offset failed and tier 2 drew an outward-kinked front. Legal now
    // means what a setback actually says: inside the parcel and at least the edge's
    // setback from every FINITE boundary segment.
    // (a point ON the line — a 0' setback edge — counts as inside)
    const onOrIn = (p: Pt2) => pointInRing(p, pts) || pts.some((a, i) => distToSegment(p, a, pts[(i + 1) % pts.length]) <= 0.05)
    const legal = flat.every((p) => onOrIn(p) && pts.every((a, i) => {
      const dist = setbacks[edgeOf[i]]
      return !(dist > 0) || distToSegment(p, a, pts[(i + 1) % pts.length]) >= dist - 0.05
    }))
    if (flat.length >= 3 && Math.abs(ringArea(flat)) > 1e-6 && legal && !ringSelfIntersects(flat)) return mitered
  }
  // tier 2: sequential inward half-plane clips over the flattened ring
  let poly = pts
  for (const pl of planes) {
    poly = clipHalfPlane(poly, pl.nx, pl.ny, pl.d)
    if (poly.length < 3) return null
  }
  return { closed: true, nodes: poly.map((pt) => ({ id: newNodeId(), pt })) }
}

// ---------- bearings + survey formatting (metes and bounds) ----------

/** survey/civil lengths: decimal feet to the hundredth — 125.00' (never feet-inches) */
export function formatSurveyFt(v: number): string {
  const r = Math.round(v * 100) / 100
  return `${(Object.is(r, -0) ? 0 : r).toFixed(2)}'`
}

/** an angle as full DMS — 47°30'12" (seconds always printed: Matt's resolved Q4) */
export function formatDmsAngle(deg: number): string {
  const total = Math.round(Math.abs(deg) * 3600)
  const d = Math.floor(total / 3600)
  const m = Math.floor(total / 60) % 60
  const s = total % 60
  const pad = (x: number) => String(x).padStart(2, '0')
  return `${d}°${pad(m)}'${pad(s)}"`
}

/**
 * Quadrant bearing of a plan direction — N 47°30'12" E — against TRUE north
 * (`northRotationDeg` = degrees CCW from project +Y to true north). Direction
 * follows the drawn path (a→b), the traverse convention.
 */
export function formatBearing(dir: Pt2, northRotationDeg = 0): string {
  const az = ((Math.atan2(dir[0], dir[1]) * 180) / Math.PI + northRotationDeg + 720) % 360
  if (az <= 90) return `N ${formatDmsAngle(az)} E`
  if (az <= 180) return `S ${formatDmsAngle(180 - az)} E`
  if (az < 270) return `S ${formatDmsAngle(az - 180)} W`
  return `N ${formatDmsAngle(360 - az)} W`
}

export interface SiteEdgeInfo {
  index: number
  fromNodeId: NodeId
  a: Pt2
  b: Pt2
  /** true arc-length (arc edges measure the arc, the pathOps convention) */
  lengthFt: number
  role: SiteEdgeRole | null
  setbackFt: number
  /** label anchor ON the true curve (arc midpoint for curved courses) */
  mid: Pt2
  /** unit chord direction — the label's rotation axis */
  dir: Pt2
  /** unit normal pointing AWAY from the parcel interior — labels sit outside */
  outward: Pt2
  /** "N 47°30'12\" E", or the curve data line for an arc course */
  bearingText: string
  /** "125.00'" (arc courses: the arc length) */
  lengthText: string
}

/** per-edge metes-and-bounds derivation — everything a label renderer needs */
export function siteEdgeInfos(rec: SiteRecord, northRotationDeg = 0): SiteEdgeInfo[] {
  const n = rec.path.nodes.length
  const nEdges = edgeCount(rec.path)
  const ccw = pathArea(rec.path) >= 0
  const setbacks = rec.kind === 'propertyLine' ? resolveEdgeSetbacksFt(rec) : []
  const out: SiteEdgeInfo[] = []
  for (let e = 0; e < nEdges; e++) {
    const node = rec.path.nodes[e]
    const a = node.pt, b = rec.path.nodes[(e + 1) % n].pt
    const chord: Pt2 = [b[0] - a[0], b[1] - a[1]]
    const chordLen = Math.hypot(chord[0], chord[1])
    if (chordLen < 1e-9 && !node.bulge) continue
    const dir: Pt2 = chordLen > 1e-9 ? [chord[0] / chordLen, chord[1] / chordLen] : [1, 0]
    // interior is LEFT of travel on a CCW ring — outward is the other side
    const outward: Pt2 = ccw ? [dir[1], -dir[0]] : [-dir[1], dir[0]]
    const arc = bulgeToArc(a, b, node.bulge)
    const lengthFt = arc ? arcLengthOf(arc) : chordLen
    const mid: Pt2 = arc ? arcMidpoint(arc) : [(a[0] + b[0]) / 2, (a[1] + b[1]) / 2]
    out.push({
      index: e,
      fromNodeId: node.id,
      a, b, lengthFt,
      role: rec.edgeRoles?.[node.id] ?? null,
      setbackFt: setbacks[e] ?? 0,
      mid, dir, outward,
      bearingText: arc
        ? `Δ=${formatDmsAngle((Math.abs(arc.sweep) * 180) / Math.PI)} R=${formatSurveyFt(arc.r)}`
        : formatBearing(dir, northRotationDeg),
      lengthText: arc ? `L=${formatSurveyFt(lengthFt)}` : formatSurveyFt(lengthFt),
    })
  }
  return out
}

// ---------- average lot depth / width (derived reference dims, 2026-08-21) ----------
// Matt: on a site plan, show a depth line (front-midpoint → rear-midpoint) and a
// width line perpendicular through the depth line's own midpoint, stopped at the
// side property lines — both DERIVED at read time from the classified edges
// (edgeRoles), never stored. "Midpoint" walks the flattened, arc-true chain of
// every edge sharing a role to its half-length station, so a jagged multi-segment
// frontage still resolves to ONE point, not an average of separate edge midpoints.

/** point at `frac` (0..1) along a flattened point chain, by arc length */
function pointAlongChain(pts: Pt2[], frac: number): Pt2 | null {
  if (pts.length < 2) return null
  let total = 0
  const lens: number[] = []
  for (let i = 0; i < pts.length - 1; i++) {
    const l = Math.hypot(pts[i + 1][0] - pts[i][0], pts[i + 1][1] - pts[i][1])
    lens.push(l); total += l
  }
  if (total < 1e-9) return pts[0]
  const target = total * frac
  let acc = 0
  for (let i = 0; i < lens.length; i++) {
    if (acc + lens[i] >= target - 1e-9) {
      const t = lens[i] > 1e-9 ? Math.max(0, Math.min(1, (target - acc) / lens[i])) : 0
      return [pts[i][0] + (pts[i + 1][0] - pts[i][0]) * t, pts[i][1] + (pts[i + 1][1] - pts[i][1]) * t]
    }
    acc += lens[i]
  }
  return pts[pts.length - 1]
}

/**
 * The length-weighted midpoint of every edge tagged `role`, walking edges in ring
 * order (a multi-segment run of the same role is expected to be contiguous, the
 * Site Analysis "one setback per role" convention). Null when the role isn't
 * assigned to any edge.
 */
function roleChainMidpoint(rec: SiteRecord, role: SiteEdgeRole): Pt2 | null {
  const n = rec.path.nodes.length
  const nEdges = edgeCount(rec.path)
  const chain: Pt2[] = []
  for (let e = 0; e < nEdges; e++) {
    const node = rec.path.nodes[e]
    if (rec.edgeRoles?.[node.id] !== role) continue
    const a = node.pt, b = rec.path.nodes[(e + 1) % n].pt
    const pts = flatEdge(a, b, node.bulge)
    if (chain.length) chain.push(...pts.slice(1))
    else chain.push(...pts)
  }
  return pointAlongChain(chain, 0.5)
}

/**
 * The nearest crossing (by |t| along `dir` from `origin`) of the infinite line
 * origin + t·dir against every edge tagged `role` — the "stop at the side
 * property line" resolution for the width line. Arc-aware (walks the flattened
 * chain per edge, same as every other site-domain crossing derivation).
 */
function nearestRoleCrossing(rec: SiteRecord, origin: Pt2, dir: Pt2, role: SiteEdgeRole): Pt2 | null {
  const n = rec.path.nodes.length
  const nEdges = edgeCount(rec.path)
  let best: { t: number; pt: Pt2 } | null = null
  for (let e = 0; e < nEdges; e++) {
    const node = rec.path.nodes[e]
    if (rec.edgeRoles?.[node.id] !== role) continue
    const a = node.pt, b = rec.path.nodes[(e + 1) % n].pt
    const pts = flatEdge(a, b, node.bulge)
    for (let i = 0; i < pts.length - 1; i++) {
      const p = pts[i], q = pts[i + 1]
      const ex = q[0] - p[0], ey = q[1] - p[1]
      const denom = dir[0] * ey - dir[1] * ex
      if (Math.abs(denom) < 1e-12) continue
      const dx0 = p[0] - origin[0], dy0 = p[1] - origin[1]
      const s = (dir[1] * dx0 - dir[0] * dy0) / denom
      if (s < -1e-6 || s > 1 + 1e-6) continue
      const t = (dx0 * ey - ex * dy0) / denom
      if (!best || Math.abs(t) < Math.abs(best.t)) {
        best = { t, pt: [origin[0] + dir[0] * t, origin[1] + dir[1] * t] }
      }
    }
  }
  return best ? best.pt : null
}

export interface SiteAverageDim {
  a: Pt2
  b: Pt2
  lengthFt: number
  label: string
}

export interface SiteAverageLotDims {
  depth: SiteAverageDim | null
  width: SiteAverageDim | null
}

/**
 * The "Average Lot Depth" / "Average Lot Width" reference dims: depth runs
 * front-midpoint → rear-midpoint; width is perpendicular through the depth line's
 * own midpoint, stopped at the nearest side1/side2-tagged crossing on each side.
 * Both null when the roles needed for them aren't assigned (front/rear, or
 * side1/side2 once a depth line exists) — a convenience overlay, never a
 * requirement of a property line record.
 */
export function siteAverageLotDims(rec: SiteRecord): SiteAverageLotDims {
  if (rec.kind !== 'propertyLine' || !rec.path.closed || rec.path.nodes.length < 3) {
    return { depth: null, width: null }
  }
  const frontMid = roleChainMidpoint(rec, 'front')
  const rearMid = roleChainMidpoint(rec, 'rear')
  let depth: SiteAverageDim | null = null
  if (frontMid && rearMid) {
    const lengthFt = Math.hypot(rearMid[0] - frontMid[0], rearMid[1] - frontMid[1])
    if (lengthFt > 1e-6) depth = { a: frontMid, b: rearMid, lengthFt, label: 'Average Lot Depth' }
  }
  let width: SiteAverageDim | null = null
  if (depth) {
    const dir: Pt2 = [(depth.b[0] - depth.a[0]) / depth.lengthFt, (depth.b[1] - depth.a[1]) / depth.lengthFt]
    const perp: Pt2 = [-dir[1], dir[0]]
    const mid: Pt2 = [(depth.a[0] + depth.b[0]) / 2, (depth.a[1] + depth.b[1]) / 2]
    const p1 = nearestRoleCrossing(rec, mid, perp, 'side1')
    const p2 = nearestRoleCrossing(rec, mid, perp, 'side2')
    if (p1 && p2) {
      const lengthFt = Math.hypot(p2[0] - p1[0], p2[1] - p1[1])
      if (lengthFt > 1e-6) width = { a: p1, b: p2, lengthFt, label: 'Average Lot Width' }
    }
  }
  return { depth, width }
}

// ---------- survey calls — metes-and-bounds INPUT (bim-plan/14 r5) ----------
// The Revit-style "enter distances and bearings" dialog's math: structured calls
// in, a traverse (ring + misclosure) out, plus the inverse (calls FROM an existing
// record) so editing round-trips. Curve courses are entered the plat-curve-table
// way — CHORD bearing + CHORD distance + radius + turn side — because that tuple
// is unambiguous and always present on a plat, unlike arc length whose paired
// bearing (chord vs tangent) varies by surveyor.

export interface SurveyCall {
  ns: 'N' | 'S'
  /** DMS of the quadrant angle, 0–90° */
  deg: number
  min: number
  sec: number
  ew: 'E' | 'W'
  /** course length, survey decimal feet — the CHORD distance for a curve */
  distanceFt: number
  /** curve course when > 0 (circular, minor arc) */
  radiusFt?: number
  /** which way the course turns along travel — 'L' = CCW (positive bulge), 'R' = CW */
  side?: 'L' | 'R'
}

/** quadrant DMS → azimuth degrees (0 = true north, clockwise positive) */
export function surveyAzimuthDeg(call: Pick<SurveyCall, 'ns' | 'deg' | 'min' | 'sec' | 'ew'>): number {
  const q = call.deg + call.min / 60 + call.sec / 3600
  if (call.ns === 'N') return call.ew === 'E' ? q : (360 - q) % 360
  return call.ew === 'E' ? 180 - q : 180 + q
}

/** the call's unit chord direction in PROJECT space — the exact inverse of
 *  `formatBearing` (az = atan2(dx, dy) + north ⇒ [dx, dy] = sin/cos of az − north) */
export function surveyCallDir(call: SurveyCall, northRotationDeg = 0): Pt2 {
  const rad = ((surveyAzimuthDeg(call) - northRotationDeg) * Math.PI) / 180
  return [Math.sin(rad), Math.cos(rad)]
}

/** chord + radius + turn side → bulge (minor arc; a chord longer than the diameter
 *  clamps to the semicircle). 0 = straight / not a valid curve. */
export function bulgeFromChordRadius(chordFt: number, radiusFt: number, side: 'L' | 'R'): number {
  if (!(radiusFt > 0) || !(chordFt > 0)) return 0
  const sweep = 2 * Math.asin(Math.min(1, chordFt / (2 * radiusFt)))
  // positive bulge = CCW travel (arc.ts) = turning LEFT along the course
  return Math.tan(sweep / 4) * (side === 'R' ? -1 : 1)
}

/** structured quadrant-bearing decompose of a chord direction (the table
 *  `formatBearing` prints, as numbers — rounded to whole seconds, the survey grain) */
export function bearingPartsOf(dir: Pt2, northRotationDeg = 0): Pick<SurveyCall, 'ns' | 'deg' | 'min' | 'sec' | 'ew'> {
  const az = ((Math.atan2(dir[0], dir[1]) * 180) / Math.PI + northRotationDeg + 720) % 360
  let ns: 'N' | 'S', ew: 'E' | 'W', q: number
  if (az <= 90) { ns = 'N'; ew = 'E'; q = az }
  else if (az <= 180) { ns = 'S'; ew = 'E'; q = 180 - az }
  else if (az < 270) { ns = 'S'; ew = 'W'; q = az - 180 }
  else { ns = 'N'; ew = 'W'; q = 360 - az }
  const total = Math.round(q * 3600)
  return { ns, ew, deg: Math.floor(total / 3600), min: Math.floor(total / 60) % 60, sec: total % 60 }
}

export interface SurveyTraverse {
  /** ring vertices v0…v(n−1); entry i carries call i's bulge (edge i = vi → v(i+1)) */
  nodes: { pt: Pt2; bulge?: number }[]
  /** where the LAST call lands — on a closed traverse, back at v0 */
  endPt: Pt2
  /** |endPt − v0| — the closure error the preview must surface */
  misclosureFt: number
  /** total course length (arc-true for curves) */
  perimeterFt: number
}

/**
 * Walk the calls from a point of beginning. The ring is v0…v(n−1) with the LAST
 * edge drawn v(n−1) → v0 — so any misclosure is absorbed by that closing edge,
 * never scattered; the caller decides whether the reported misclosure is
 * acceptable before committing.
 */
export function traverseFromSurveyCalls(
  calls: SurveyCall[],
  startPt: Pt2,
  northRotationDeg = 0,
): SurveyTraverse {
  const nodes: SurveyTraverse['nodes'] = []
  let cur: Pt2 = [startPt[0], startPt[1]]
  let perimeterFt = 0
  for (const call of calls) {
    const bulge = call.radiusFt ? bulgeFromChordRadius(call.distanceFt, call.radiusFt, call.side ?? 'L') : 0
    nodes.push(bulge ? { pt: cur, bulge } : { pt: cur })
    const dir = surveyCallDir(call, northRotationDeg)
    const next: Pt2 = [cur[0] + dir[0] * call.distanceFt, cur[1] + dir[1] * call.distanceFt]
    const arc = bulge ? bulgeToArc(cur, next, bulge) : null
    perimeterFt += arc ? arcLengthOf(arc) : call.distanceFt
    cur = next
  }
  return {
    nodes,
    endPt: cur,
    misclosureFt: Math.hypot(cur[0] - startPt[0], cur[1] - startPt[1]),
    perimeterFt,
  }
}

/** the calls list an existing record's ring reads back as — the edit-mode round
 *  trip (quantized to whole seconds + hundredth-feet, the survey grain, so an
 *  apply re-snaps the ring onto survey precision) */
export function surveyCallsOf(rec: SiteRecord, northRotationDeg = 0): SurveyCall[] {
  const n = rec.path.nodes.length
  const out: SurveyCall[] = []
  for (let e = 0; e < edgeCount(rec.path); e++) {
    const node = rec.path.nodes[e]
    const a = node.pt, b = rec.path.nodes[(e + 1) % n].pt
    const chord = Math.hypot(b[0] - a[0], b[1] - a[1])
    if (chord < 1e-9) continue
    const dir: Pt2 = [(b[0] - a[0]) / chord, (b[1] - a[1]) / chord]
    const arc = bulgeToArc(a, b, node.bulge)
    out.push({
      ...bearingPartsOf(dir, northRotationDeg),
      distanceFt: Math.round(chord * 100) / 100,
      ...(arc ? { radiusFt: Math.round(arc.r * 100) / 100, side: arc.sweep >= 0 ? 'L' as const : 'R' as const } : {}),
    })
  }
  return out
}

// ---------- the vertical crossing (sections/elevations) ----------

export interface SiteCrossing {
  /** station along the section line from its `a` end, feet */
  u: number
  /** what crosses here: the property line, the setback envelope, an easement */
  tag: 'PL' | 'SB' | 'ESMT'
}

/**
 * The shallowest a view line may cross a site line and still show it: 45°. A
 * vertical view reads a P.L. / SETBACK / easement as a plane standing square to
 * the drawing; a line that runs ALONG the yard instead of across it slices the
 * site line at a glancing angle, so the crossing lands somewhere in the middle of
 * the drawing and a setback reads as setback ÷ sin(angle) — Santiago Elevation E
 * ran 4° off its side property line and drew an 83' "REQ'D SIDE YARD SETBACK".
 */
export const MIN_SITE_CROSSING_SIN = Math.SQRT1_2

/** finite section-segment × finite site-edge intersections (the grids-in-verticals
 *  model: a crossing appears only where the drawn line actually crosses it, and
 *  only where it crosses at 45° or steeper — `MIN_SITE_CROSSING_SIN`) */
export function siteCrossingsForVertical(
  model: ModelDoc,
  scope: { line: { a: Pt2; b: Pt2 } },
): SiteCrossing[] {
  const { a: A, b: B } = scope.line
  const dx = B[0] - A[0], dy = B[1] - A[1]
  const len = Math.hypot(dx, dy)
  if (len < 1e-9) return []
  const out: SiteCrossing[] = []
  const cross = (p: Pt2, q: Pt2, tag: SiteCrossing['tag']) => {
    const ex = q[0] - p[0], ey = q[1] - p[1]
    const den = dx * ey - dy * ex
    if (Math.abs(den) < 1e-12) return
    if (Math.abs(den) < MIN_SITE_CROSSING_SIN * len * Math.hypot(ex, ey)) return
    const t = ((p[0] - A[0]) * ey - (p[1] - A[1]) * ex) / den
    const s = ((p[0] - A[0]) * dy - (p[1] - A[1]) * dx) / den
    if (t < 0 || t > 1 || s < 0 || s > 1) return
    out.push({ u: t * len, tag })
  }
  const walkRing = (pts: Pt2[], closed: boolean, tag: SiteCrossing['tag']) => {
    for (let i = 0; i < pts.length - (closed ? 0 : 1); i++) {
      cross(pts[i], pts[(i + 1) % pts.length], tag)
    }
  }
  for (const rec of listSiteRecords(model)) {
    const flat = flattenSiteRing(rec)
    walkRing(flat.pts, rec.path.closed, rec.kind === 'propertyLine' ? 'PL' : 'ESMT')
    if (rec.kind === 'propertyLine') {
      const env = siteEnvelope(rec)
      if (env) walkRing(flattenPathPts(env), true, 'SB')
    }
  }
  return out.sort((x, y) => x.u - y.u)
}
